/**
 * Build Chart.js (v2.9.x) configuration objects from widget configs.
 * Supports: bar (vertical/horizontal, grouped, stacked, stacked_100),
 * line, area, pie, donut, scatter, radar.
 */

import {
  groupBy, groupByTwo, sortGrouped, isDateField, sortByDate,
} from './aggregations'
import { CHART_PALETTE, pickColors, hexToRgba } from './theme'
import { compactNumber, formatAxisTick } from './formatting'

/** True when the widget renders a numeric value scale on the Y axis (vertical charts). */
export function widgetHasNumericYAxis(cfg) {
  if (!cfg) return false
  const type = String(cfg.type || 'bar').toLowerCase()
  if (type === 'pie' || type === 'donut' || type === 'doughnut' || type === 'table' || type === 'radar') {
    return false
  }
  if (type === 'bar' || type === 'horizontal_bar') {
    const orient = String(cfg.orientation || 'vertical').toLowerCase()
    if (orient === 'horizontal' || type === 'horizontal_bar') return false
    if (cfg.stack_mode === 'stacked_100') return false
  }
  return ['bar', 'line', 'area', 'scatter'].includes(type)
}

/** Horizontal bar charts put numeric values on the X axis. */
export function widgetHasNumericXAxis(cfg) {
  if (!cfg) return false
  const type = String(cfg.type || 'bar').toLowerCase()
  if (type !== 'bar' && type !== 'horizontal_bar') return false
  const orient = String(cfg.orientation || 'vertical').toLowerCase()
  return orient === 'horizontal' || type === 'horizontal_bar'
}

function borderDashFromStyle(style) {
  if (style === 'dashed') return [6, 3]
  if (style === 'dotted') return [2, 2]
  return []
}

// Safe legend click — refuses to hide the last visible series/slice
function safeLegendOnClick(e, legendItem) {
  // `this` is the legend instance in Chart.js v2
  const ci = this.chart
  const type = ci.config.type
  const isArc = (type === 'pie' || type === 'doughnut' || type === 'polarArea')

  if (isArc) {
    const idx = legendItem.index
    const meta = ci.getDatasetMeta(0)
    const arc = meta.data[idx]
    const wouldHide = !arc.hidden === true
    if (wouldHide) {
      let visible = 0
      meta.data.forEach(a => { if (!a.hidden) visible++ })
      if (visible <= 1) return
    }
    arc.hidden = !arc.hidden
    ci.update()
  } else {
    const dsIdx = legendItem.datasetIndex
    const meta = ci.getDatasetMeta(dsIdx)
    const wouldHide = meta.hidden === null ? !ci.data.datasets[dsIdx].hidden : !meta.hidden
    if (wouldHide) {
      const visible = ci.data.datasets.reduce((n, ds, i) => {
        const m = ci.getDatasetMeta(i)
        const hidden = m.hidden === null ? ds.hidden : m.hidden
        return n + (hidden ? 0 : 1)
      }, 0)
      if (visible <= 1) return
    }
    meta.hidden = meta.hidden === null ? !ci.data.datasets[dsIdx].hidden : null
    ci.update()
  }
}

function buildScalesOptions(cfg, themeColors, compact) {
  const isHoriz = cfg.orientation === 'horizontal' || cfg.type === 'horizontal_bar'
  const xCfg = isHoriz ? (cfg.y_axis || {}) : (cfg.x_axis || {})
  const yCfg = isHoriz ? (cfg.x_axis || {}) : (cfg.y_axis || {})

  const muted = themeColors.textMuted
  const grid = themeColors.border + '66'
  const tickSize = compact ? 10 : 11

  const xAxis = {
    ticks: {
      fontColor: muted,
      fontSize: tickSize,
      maxRotation: xCfg.rotate_labels != null ? xCfg.rotate_labels : (isHoriz ? 0 : (compact ? 60 : 45)),
      minRotation: 0,
      autoSkip: true,
      autoSkipPadding: compact ? 12 : 8,
      maxTicksLimit: compact ? 8 : undefined,
    },
    gridLines: { color: grid, drawBorder: false, zeroLineColor: grid },
  }
  if (xCfg.min != null) xAxis.ticks.min = xCfg.min
  if (xCfg.max != null) xAxis.ticks.max = xCfg.max
  if (isHoriz && widgetHasNumericXAxis(cfg)) {
    const tickFormat = xCfg.tick_format || 'full'
    xAxis.ticks.callback = (val) => formatAxisTick(val, tickFormat)
  }
  if (xCfg.label) {
    xAxis.scaleLabel = { display: true, labelString: xCfg.label, fontColor: muted, fontSize: 12 }
  }

  const yAxis = {
    ticks: {
      fontColor: muted,
      fontSize: tickSize,
      beginAtZero: !isHoriz,
      autoSkip: true,
      maxTicksLimit: isHoriz && compact ? 10 : undefined,
    },
    gridLines: { color: grid, drawBorder: false, zeroLineColor: grid },
  }
  if (yCfg.min != null) yAxis.ticks.min = yCfg.min
  if (yCfg.max != null) yAxis.ticks.max = yCfg.max
  if (!isHoriz && widgetHasNumericYAxis(cfg)) {
    const tickFormat = yCfg.tick_format || 'full'
    yAxis.ticks.callback = (val) => formatAxisTick(val, tickFormat)
  }
  if (yCfg.label) {
    yAxis.scaleLabel = { display: true, labelString: yCfg.label, fontColor: muted, fontSize: 12 }
  }
  return { xAxes: [xAxis], yAxes: [yAxis] }
}

function buildLegendOptions(cfg, themeColors) {
  const leg = cfg.legend || {}
  const enabled = leg.enabled !== false
  if (!enabled) return { display: false }
  return {
    display: true,
    position: leg.position || 'bottom',
    labels: { fontColor: themeColors.text, fontSize: 11, padding: 12 },
    onClick: safeLegendOnClick,
  }
}

function chartLayoutPadding(cfg, compact) {
  const isHoriz = cfg.orientation === 'horizontal' || cfg.type === 'horizontal_bar'
  if (compact && isHoriz) {
    return { top: 8, right: 16, bottom: 8, left: 8 }
  }
  if (compact) {
    return { top: 6, right: 12, bottom: 6, left: 6 }
  }
  if (isHoriz) {
    return { top: 10, right: 24, bottom: 8, left: 12 }
  }
  return { top: 8, right: 16, bottom: 5, left: 8 }
}

function buildBaseOptions(cfg, themeColors, compact) {
  const leg = buildLegendOptions(cfg, themeColors)
  if (compact && leg.display !== false) {
    leg.position = 'bottom'
  }
  return {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: chartLayoutPadding(cfg, compact) },
    legend: leg,
    tooltips: {
      backgroundColor: themeColors.surface,
      titleFontColor: themeColors.text,
      bodyFontColor: themeColors.text,
      borderColor: themeColors.border,
      borderWidth: 1,
      callbacks: {
        label(ctx, data) {
          const ds = data.datasets[ctx.datasetIndex]
          const raw = ds.data[ctx.index]
          const val = (raw && typeof raw === 'object') ? raw.y : raw
          const label = ds.label || data.labels[ctx.index] || ''
          return (label ? label + ': ' : '') + compactNumber(Number(val) || 0)
        },
      },
    },
    scales: buildScalesOptions(cfg, themeColors, compact),
    animation: { duration: 400 },
  }
}

function buildArcOptions(cfg, themeColors, compact) {
  const leg = cfg.legend || {}
  const enabled = leg.enabled !== false
  return {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: compact ? { top: 4, right: 4, bottom: 4, left: 4 } : { top: 8, right: 8, bottom: 8, left: 8 } },
    legend: enabled
      ? {
          display: true,
          position: compact ? 'bottom' : (leg.position || 'right'),
          labels: { fontColor: themeColors.text, fontSize: 11, padding: 12 },
          onClick: safeLegendOnClick,
        }
      : { display: false },
    tooltips: {
      backgroundColor: themeColors.surface,
      titleFontColor: themeColors.text,
      bodyFontColor: themeColors.text,
      borderColor: themeColors.border,
      borderWidth: 1,
      callbacks: {
        label(ctx, data) {
          const val = data.datasets[0].data[ctx.index]
          const lab = data.labels[ctx.index]
          const total = data.datasets[0].data.reduce((a, b) => a + b, 0)
          const pct = total > 0 ? ((val / total) * 100).toFixed(1) : 0
          return `${lab}: ${compactNumber(val)} (${pct}%)`
        },
      },
    },
    animation: { duration: 400 },
  }
}

/**
 * A minimal data-label plugin (Chart.js v2) that draws values on datasets
 * when widget cfg has `show_data_labels: true`. Pie/donut show percentages
 * inside slices; bar/line show compact values near data points.
 */
export const dataLabelsPlugin = {
  id: 'cfgDashboardDataLabels',
  afterDatasetsDraw(chart) {
    const cfg = chart.options._dashCfg
    if (!cfg || !cfg.show_data_labels) return
    const type = chart.config.type
    const ctx = chart.ctx
    ctx.save()
    ctx.font = 'bold 10px system-ui, -apple-system, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    if (type === 'pie' || type === 'doughnut') {
      const meta = chart.getDatasetMeta(0)
      const total = chart.data.datasets[0].data.reduce((a, b) => a + (Number(b) || 0), 0)
      meta.data.forEach((arc, i) => {
        if (arc.hidden) return
        const v = Number(chart.data.datasets[0].data[i]) || 0
        if (total <= 0 || v <= 0) return
        const pct = ((v / total) * 100).toFixed(1)
        const { x, y } = arc.tooltipPosition()
        ctx.fillStyle = '#ffffff'
        ctx.fillText(pct + '%', x, y)
      })
    } else if (type === 'bar' || type === 'horizontalBar' || type === 'line') {
      const themeText = (chart.options._dashThemeText) || '#1e293b'
      ctx.fillStyle = themeText
      chart.data.datasets.forEach((ds, di) => {
        const meta = chart.getDatasetMeta(di)
        if (meta.hidden) return
        meta.data.forEach((el, i) => {
          const v = Number(ds.data[i])
          if (!isFinite(v)) return
          const label = compactNumber(v)
          const pos = el.tooltipPosition()
          let x = pos.x, y = pos.y
          if (type === 'horizontalBar') { x = pos.x + 14; ctx.textAlign = 'left' }
          else { y = pos.y - 8; ctx.textAlign = 'center' }
          ctx.fillText(label, x, y)
        })
      })
    }
    ctx.restore()
  },
}

/**
 * Build a Chart.js v2 config for a single widget definition.
 * Returns { chartType, data, options } or null when unsupported.
 */
export function buildChartConfig(data, cfg, i, themeColors, compact) {
  const isCompact = !!compact
  const type = cfg.type || 'bar'
  const agg = cfg.agg || 'sum'
  const userColor = cfg.color || CHART_PALETTE[i % CHART_PALETTE.length]
  const userPalette = cfg.color_palette || null

  const attachMeta = (opts) => {
    // Keep plain primitives only — Chart.js deep-clones options via lodash.
    opts._dashCfg = { show_data_labels: !!cfg.show_data_labels }
    opts._dashThemeText = themeColors.text
    return opts
  }

  if (type === 'bar') {
    const orientation = cfg.orientation || 'vertical'
    const stackMode = cfg.stack_mode || 'none'
    const groupByField = cfg.group_by || cfg.stack_by || cfg.series_by || null
    const barRadius = cfg.bar_radius || 0
    const chartType = orientation === 'horizontal' ? 'horizontalBar' : 'bar'

    if (groupByField) {
      const grouped2 = groupByTwo(data, cfg.x, groupByField, cfg.y || cfg.x, agg)
      const allX = [...new Set(data.map(d => d[cfg.x]))].sort()
      const seriesKeys = Object.keys(grouped2)
      const pal = userPalette || CHART_PALETTE
      let datasets = seriesKeys.map((s, si) => ({
        label: s,
        data: allX.map(x => grouped2[s][x] || 0),
        backgroundColor: pal[si % pal.length],
        borderColor: pal[si % pal.length],
        borderWidth: 0,
        barPercentage: 0.9,
        categoryPercentage: 0.8,
        borderRadius: barRadius,
      }))
      const opts = buildBaseOptions(cfg, themeColors, isCompact)
      if (stackMode !== 'none') {
        opts.scales.xAxes[0].stacked = true
        opts.scales.yAxes[0].stacked = true
        if (stackMode === 'stacked_100') {
          opts.scales.yAxes[0].ticks.max = 100
          for (let xi = 0; xi < allX.length; xi++) {
            let total = 0
            datasets.forEach(ds => { total += ds.data[xi] })
            if (total > 0) datasets.forEach(ds => { ds.data[xi] = (ds.data[xi] / total) * 100 })
          }
        }
      }
      return { chartType, data: { labels: allX, datasets }, options: attachMeta(opts) }
    }

    let grouped = groupBy(data, cfg.x, cfg.y || cfg.x, agg)
    const sortBars = cfg.sort_bars || cfg.sort || 'none'
    if (sortBars !== 'none') {
      grouped = sortGrouped(grouped, sortBars, cfg.top_n || cfg.limit)
    } else if (cfg.top_n) {
      grouped = sortGrouped(grouped, 'desc', cfg.top_n)
    }
    const labels = Object.keys(grouped)
    const values = Object.values(grouped)
    return {
      chartType,
      data: {
        labels,
        datasets: [{
          label: cfg.title || '',
          data: values,
          backgroundColor: userColor,
          borderColor: userColor,
          borderWidth: 0,
          barPercentage: 0.9,
          categoryPercentage: 0.8,
        }],
      },
      options: attachMeta(buildBaseOptions(cfg, themeColors, isCompact)),
    }
  }

  if (type === 'line') {
    const seriesField = cfg.series_column || cfg.series_by || null
    const lineWidth = cfg.line_width != null ? cfg.line_width : 2
    const tension = cfg.smooth ? 0.4 : 0
    const showPts = cfg.show_points
    const dash = borderDashFromStyle(cfg.line_style || 'solid')

    if (seriesField) {
      const grouped2 = groupByTwo(data, cfg.x, seriesField, cfg.y || cfg.x, agg)
      const allX = [...new Set(data.map(d => d[cfg.x]))]
      if (isDateField(data, cfg.x)) allX.sort((a, b) => new Date(a) - new Date(b))
      else allX.sort()
      const pal = userPalette || CHART_PALETTE
      const datasets = Object.keys(grouped2).map((s, si) => ({
        label: s,
        data: allX.map(x => grouped2[s][x] || 0),
        borderColor: pal[si % pal.length],
        backgroundColor: 'transparent',
        borderWidth: lineWidth,
        borderDash: dash,
        lineTension: tension,
        pointRadius: showPts ? 3 : (allX.length > 30 ? 0 : 2),
        pointBackgroundColor: pal[si % pal.length],
        fill: false,
      }))
      return {
        chartType: 'line',
        data: { labels: allX, datasets },
        options: attachMeta(buildBaseOptions(cfg, themeColors, isCompact)),
      }
    }

    let grouped = groupBy(data, cfg.x, cfg.y || cfg.x, agg)
    let labels = Object.keys(grouped)
    let values = Object.values(grouped)
    if (isDateField(data, cfg.x)) {
      const sorted = sortByDate(labels, values)
      labels = sorted.labels; values = sorted.values
    }
    return {
      chartType: 'line',
      data: {
        labels,
        datasets: [{
          label: cfg.title || '',
          data: values,
          borderColor: userColor,
          backgroundColor: 'transparent',
          borderWidth: lineWidth,
          borderDash: dash,
          lineTension: tension,
          pointRadius: showPts ? 3 : (labels.length > 30 ? 0 : 2),
          pointBackgroundColor: userColor,
          fill: false,
        }],
      },
      options: attachMeta(buildBaseOptions(cfg, themeColors, isCompact)),
    }
  }

  if (type === 'area') {
    const fillOpacity = cfg.fill_opacity != null ? cfg.fill_opacity : 0.3
    const lineWidth = cfg.line_width != null ? cfg.line_width : 2
    const tension = cfg.smooth ? 0.4 : 0
    const showPts = cfg.show_points
    const dash = borderDashFromStyle(cfg.line_style || 'solid')
    let grouped = groupBy(data, cfg.x, cfg.y || cfg.x, agg)
    let labels = Object.keys(grouped)
    let values = Object.values(grouped)
    if (isDateField(data, cfg.x)) {
      const sorted = sortByDate(labels, values)
      labels = sorted.labels; values = sorted.values
    }
    const bg = hexToRgba(userColor, fillOpacity)
    return {
      chartType: 'line',
      data: {
        labels,
        datasets: [{
          label: cfg.title || '',
          data: values,
          borderColor: userColor,
          backgroundColor: bg,
          borderWidth: lineWidth,
          borderDash: dash,
          lineTension: tension,
          pointRadius: showPts ? 3 : (labels.length > 30 ? 0 : 2),
          pointBackgroundColor: userColor,
          fill: true,
        }],
      },
      options: attachMeta(buildBaseOptions(cfg, themeColors, isCompact)),
    }
  }

  if (type === 'pie' || type === 'donut' || type === 'doughnut') {
    let grouped = groupBy(data, cfg.x, cfg.y || cfg.x, agg)
    if (cfg.top_n) grouped = sortGrouped(grouped, 'desc', cfg.top_n)
    const labels = Object.keys(grouped)
    const values = Object.values(grouped)
    const isDonut = type !== 'pie'
    const opts = buildArcOptions(cfg, themeColors, isCompact)
    if (isDonut) {
      const innerRadius = cfg.inner_radius != null ? cfg.inner_radius : 0.5
      opts.cutoutPercentage = Math.round(innerRadius * 100)
    } else {
      opts.cutoutPercentage = 0
    }
    return {
      chartType: 'doughnut',
      data: {
        labels,
        datasets: [{
          data: values,
          backgroundColor: pickColors(labels.length, userPalette),
          borderColor: themeColors.surface,
          borderWidth: 2,
        }],
      },
      options: attachMeta(opts),
    }
  }

  if (type === 'scatter') {
    const ptSize = cfg.point_size != null ? cfg.point_size : 5
    const ptOpacity = cfg.point_opacity != null ? cfg.point_opacity : 0.8
    const bg = hexToRgba(userColor, ptOpacity)
    const points = data.map(d => ({
      x: parseFloat(d[cfg.x]) || 0,
      y: parseFloat(d[cfg.y]) || 0,
    }))
    const opts = buildBaseOptions(cfg, themeColors, isCompact)
    opts.scales.xAxes[0].type = 'linear'
    opts.scales.xAxes[0].position = 'bottom'
    return {
      chartType: 'scatter',
      data: {
        datasets: [{
          label: cfg.title || '',
          data: points,
          backgroundColor: bg,
          borderColor: userColor,
          borderWidth: 1,
          pointRadius: ptSize,
          showLine: false,
        }],
      },
      options: attachMeta(opts),
    }
  }

  if (type === 'radar') {
    const grouped = groupBy(data, cfg.x, cfg.y || cfg.x, agg)
    const labels = Object.keys(grouped)
    const values = Object.values(grouped)
    return {
      chartType: 'radar',
      data: {
        labels,
        datasets: [{
          label: cfg.title || '',
          data: values,
          borderColor: userColor,
          backgroundColor: hexToRgba(userColor, 0.2),
          pointBackgroundColor: userColor,
          borderWidth: 2,
        }],
      },
      options: attachMeta({
        responsive: true,
        maintainAspectRatio: false,
        legend: buildLegendOptions(cfg, themeColors),
        scale: {
          gridLines: { color: themeColors.border + '66' },
          pointLabels: { fontColor: themeColors.text, fontSize: 11 },
          ticks: { fontColor: themeColors.textMuted, fontSize: 10 },
        },
        animation: { duration: 400 },
      }),
    }
  }

  // Fallback: render as bar
  const grouped = groupBy(data, cfg.x, cfg.y || cfg.x, agg)
  const labels = Object.keys(grouped)
  const values = Object.values(grouped)
  return {
    chartType: 'bar',
    data: {
      labels,
      datasets: [{
        label: cfg.title || '',
        data: values,
        backgroundColor: userColor,
      }],
    },
    options: attachMeta(buildBaseOptions(cfg, themeColors, isCompact)),
  }
}
